import { t } from "@lingui/macro";
import { useTheme } from "@shopify/restyle";
import { useEffect, useRef, useState } from "react";
import {
  Animated,
  Easing,
  Platform,
  StyleSheet,
  useWindowDimensions,
} from "react-native";

import { SupportedLanguage } from "@whoosh/design-system/src/components/controls";
import { Loader } from "@whoosh/design-system/src/components/display";
import { AnimationDurations } from "@whoosh/design-system/src/theme/animations";

const CIRCLE_DIAMETER = 160;
const CIRCLE_OVERSCAN = 1.1;

type LocaleTransitionOverlayProps = {
  appliedLanguage: SupportedLanguage;
  contentIsLoading: boolean;
  pendingLanguage: SupportedLanguage | null;
  onCovered: (language: SupportedLanguage) => void;
  onFinished: (language: SupportedLanguage) => void;
};

/**
 * Expands a circular loading surface over the current UI before applying a
 * locale, then contracts it after the remounted route has loaded and laid out.
 */
export const LocaleTransitionOverlay = ({
  appliedLanguage,
  contentIsLoading,
  pendingLanguage,
  onCovered,
  onFinished,
}: LocaleTransitionOverlayProps) => {
  const { colors } = useTheme();
  const { height, width } = useWindowDimensions();
  const coveredScale =
    (Math.hypot(width, height) / CIRCLE_DIAMETER) * CIRCLE_OVERSCAN;
  const progress = useRef(new Animated.Value(0)).current;
  const animation = useRef<Animated.CompositeAnimation | null>(null);
  const targetLanguage = useRef<SupportedLanguage | null>(null);
  const [visibleLanguage, setVisibleLanguage] =
    useState<SupportedLanguage | null>(null);

  useEffect(() => {
    if (!pendingLanguage) {
      return;
    }

    targetLanguage.current = pendingLanguage;
    progress.setValue(0);
    setVisibleLanguage(pendingLanguage);
    animation.current?.stop();
    animation.current = Animated.timing(progress, {
      duration: AnimationDurations.slow01,
      easing: Easing.inOut(Easing.ease),
      toValue: 1,
      useNativeDriver: Platform.OS !== "web",
    });
    animation.current.start(({ finished }) => {
      if (finished && targetLanguage.current === pendingLanguage) {
        onCovered(pendingLanguage);
      }
    });

    return () => animation.current?.stop();
  }, [onCovered, pendingLanguage, progress]);

  useEffect(() => {
    if (pendingLanguage || !visibleLanguage) {
      return;
    }

    animation.current?.stop();
    targetLanguage.current = null;
    progress.setValue(0);
    setVisibleLanguage(null);
  }, [pendingLanguage, progress, visibleLanguage]);

  useEffect(() => {
    const newLocaleIsMounted =
      visibleLanguage &&
      pendingLanguage === visibleLanguage &&
      appliedLanguage === visibleLanguage;

    if (!newLocaleIsMounted) {
      return;
    }

    if (contentIsLoading) {
      animation.current?.stop();
      progress.setValue(1);
      return;
    }

    let secondFrame: number | undefined;
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => {
        animation.current?.stop();
        animation.current = Animated.timing(progress, {
          duration: AnimationDurations.slow01,
          easing: Easing.inOut(Easing.ease),
          toValue: 0,
          useNativeDriver: Platform.OS !== "web",
        });
        animation.current.start(({ finished }) => {
          if (finished && targetLanguage.current === visibleLanguage) {
            onFinished(visibleLanguage);
            targetLanguage.current = null;
            setVisibleLanguage(null);
          }
        });
      });
    });

    return () => {
      animation.current?.stop();
      cancelAnimationFrame(firstFrame);
      if (secondFrame !== undefined) {
        cancelAnimationFrame(secondFrame);
      }
    };
  }, [
    appliedLanguage,
    contentIsLoading,
    onFinished,
    pendingLanguage,
    progress,
    visibleLanguage,
  ]);

  if (!visibleLanguage) {
    return null;
  }

  return (
    <Animated.View
      accessibilityLabel={t`...Loading`}
      accessibilityViewIsModal
      pointerEvents="auto"
      style={[StyleSheet.absoluteFill, styles.overlay]}
      testID="locale-transition-overlay"
    >
      <Animated.View
        style={[
          styles.circle,
          {
            backgroundColor: colors["surface-primary"],
            transform: [
              {
                scale: progress.interpolate({
                  inputRange: [0, 1],
                  outputRange: [0, coveredScale],
                }),
              },
            ],
          },
        ]}
      />
      <Animated.View
        style={[
          StyleSheet.absoluteFill,
          {
            opacity: progress.interpolate({
              inputRange: [0, 0.15, 1],
              outputRange: [0, 1, 1],
            }),
          },
        ]}
      >
        <Loader />
      </Animated.View>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  circle: {
    borderRadius: CIRCLE_DIAMETER / 2,
    height: CIRCLE_DIAMETER,
    left: "50%",
    marginLeft: -CIRCLE_DIAMETER / 2,
    marginTop: -CIRCLE_DIAMETER / 2,
    position: "absolute",
    top: "50%",
    width: CIRCLE_DIAMETER,
  },
  overlay: {
    elevation: 1000,
    overflow: "hidden",
    zIndex: 1000,
  },
});
